import type { SocialProviderType } from "../types/models";

// The numbers a live post can put on the platform overlay, and the rule for
// when it may not put one there at all.
//
// `publication_stats` stores a counter nobody reported as NULL, but the
// legacy `publication_metrics` stored it as 0 — indistinguishable from a post
// that genuinely has no likes — and its history was carried over. So the
// value alone never decides whether a stat is real; the PROVIDER does too.
// Everything below exists to turn a snapshot into per-stat "known number" or
// "not knowable", so the overlay can draw a count where there is one and keep
// its skeleton button everywhere else.

/**
 * The five counters, each either a real number or `null` for "this provider
 * does not report it / nothing has been scraped yet". `null` is the overlay's
 * signal to fall back to the skeleton — a `0` is a fact and gets drawn.
 */
export type PublicationStats = {
  viewCount: number | null;
  likeCount: number | null;
  commentCount: number | null;
  saveCount: number | null;
  shareCount: number | null;
};

export type PublicationStatKey = keyof PublicationStats;

/** Every stat unknown — what a post with no snapshot row is worth. */
export const EMPTY_PUBLICATION_STATS: PublicationStats = {
  viewCount: null,
  likeCount: null,
  commentCount: null,
  saveCount: null,
  shareCount: null,
};

/**
 * What each provider actually gives us, taken from the legacy scraper
 * (klap-api `helpers/scrapecreators_helper.ts`) and confirmed against
 * production: over the last 30 days, shares and saves are non-zero ONLY on
 * TikTok — Instagram and YouTube return nothing for either (YouTube hardcodes
 * both to 0 in the helper), and LinkedIn returns no view count at all.
 * Facebook is measured since the publication-metrics engine
 * (`helpers/publication_metrics/sources.ts`): everything but saves.
 *
 * Listed per provider rather than inferred from the numbers, because a real
 * zero and an unreported zero look identical in the row.
 */
const PROVIDER_REPORTED_STATS: Record<
  SocialProviderType,
  readonly PublicationStatKey[]
> = {
  tiktok: [
    "viewCount",
    "likeCount",
    "commentCount",
    "saveCount",
    "shareCount",
  ],
  instagram: ["viewCount", "likeCount", "commentCount"],
  youtube: ["viewCount", "likeCount", "commentCount"],
  linkedin: ["likeCount", "commentCount"],
  facebook: ["viewCount", "likeCount", "commentCount", "shareCount"],
};

/**
 * Whether `provider` reports `stat` at all, i.e. whether a summed 0 from it is
 * a fact or just nothing scraped (the admin publishing dashboard shows "—" for
 * the latter, e.g. LinkedIn views).
 */
export function providerReportsStat(
  provider: string,
  stat: PublicationStatKey,
): boolean {
  // Own keys only: "toString" must not find Object.prototype's.
  if (
    !Object.prototype.hasOwnProperty.call(PROVIDER_REPORTED_STATS, provider)
  ) {
    return false;
  }
  return PROVIDER_REPORTED_STATS[provider as SocialProviderType].includes(stat);
}

/**
 * A metrics snapshot, structurally: a `publication_stats` row as the overlay
 * hook maps it, with `null` for a counter never reported (klap-core's
 * `PublicationMetricModel` satisfies it too). Typed here rather than imported
 * so this module stays a pure, dependency-free unit.
 */
export type MetricSnapshot = {
  createdAt: Date;
  viewCount: number | null;
  likeCount: number | null;
  commentCount: number | null;
  shareCount: number | null;
  saveCount: number | null;
};

/**
 * The newest snapshot of a series. The table is append-only — one row per
 * publication per daily scrape — and the two existing readers disagree on
 * direction (the core fetcher orders DESC, the analytics page re-sorts ASC and
 * takes the last), so pick by timestamp rather than by position. A row with an
 * unusable date loses to any row with a real one, and never wins alone.
 */
export function selectLatestMetric<T extends { createdAt: Date }>(
  metrics: readonly T[] | null | undefined,
): T | null {
  let latest: T | null = null;
  let latestTime = -Infinity;
  for (const metric of metrics ?? []) {
    const time = new Date(metric.createdAt).getTime();
    if (!Number.isFinite(time)) continue;
    if (time >= latestTime) {
      latest = metric;
      latestTime = time;
    }
  }
  return latest;
}

/**
 * A snapshot as the overlay reads it: every counter the provider reports and
 * the platform sent, and `null` for the rest. No snapshot at all — the post is
 * too fresh to have been scraped, its owner is off-plan, or it was never
 * tracked — leaves every stat unknown.
 */
export function selectPublicationStats(
  provider: SocialProviderType | null | undefined,
  metric: MetricSnapshot | null | undefined,
): PublicationStats {
  if (!metric || !provider) return EMPTY_PUBLICATION_STATS;

  const reported = PROVIDER_REPORTED_STATS[provider];
  if (!reported) return EMPTY_PUBLICATION_STATS;

  const read = (
    key: PublicationStatKey,
    value: number | null,
  ): number | null => {
    if (!reported.includes(key) || value === null) return null;
    // A negative counter is a sentinel, never a count: Instagram answers -1
    // for the like count of a post whose owner hid it, and the legacy
    // scraper's `|| 0` did not catch it because -1 is truthy. Unknown, not
    // zero.
    if (!Number.isFinite(value) || value < 0) return null;
    return Math.floor(value);
  };

  const stats: PublicationStats = {
    viewCount: read("viewCount", metric.viewCount),
    likeCount: read("likeCount", metric.likeCount),
    commentCount: read("commentCount", metric.commentCount),
    saveCount: read("saveCount", metric.saveCount),
    shareCount: read("shareCount", metric.shareCount),
  };

  // The provider matrix cannot see a scrape that came back half-empty: the
  // legacy helper coalesced a missing field to 0 and stored the row anyway,
  // so a dropped field was written as a real-looking zero. Views are the one
  // counter where that zero gives itself away — nobody likes, comments on,
  // saves or shares a post they did not watch — so 0 views beside any other
  // activity is a partial scrape, and the handle keeps its bare name.
  if (
    stats.viewCount === 0 &&
    [stats.likeCount, stats.commentCount, stats.saveCount, stats.shareCount].some(
      (value) => value != null && value > 0,
    )
  ) {
    stats.viewCount = null;
  }

  return stats;
}

/** Whether anything at all is knowable — the gate on drawing the real rail. */
export function hasAnyPublicationStat(
  stats: PublicationStats | null | undefined,
): boolean {
  if (!stats) return false;
  return Object.values(stats).some((value) => value != null);
}
